import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../services/api';
import { toast } from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  User,
  Mail,
  Shield,
  Lock,
  Key,
  X,
  Save,
  Filter,
  Calendar,
  FileText,
  Upload,
  Download,
  Paperclip
} from 'lucide-react';

const Users = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const queryClient = useQueryClient();

  // Estados do formulário
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'PRODUCAO',
    is_active: true,
    birth_date: '',
    hire_date: ''
  });

  // Estados para documentos
  const [selectedUserForDocuments, setSelectedUserForDocuments] = useState(null);
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const [documentFormData, setDocumentFormData] = useState({
    document_type: '',
    description: '',
    document: null
  });

  // Estados para reset de senha
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [userToDelete, setUserToDelete] = useState(null);
  const [userToPermanentDelete, setUserToPermanentDelete] = useState(null);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  // Query para buscar funcionários
  const { data: usersData, isLoading, error } = useQuery({
    queryKey: ['users', { role: selectedRole, active: selectedStatus, search: searchTerm }],
    queryFn: () => userService.getUsers({
      role: selectedRole || undefined,
      active: selectedStatus || undefined,
      limit: 50
    }),
    select: data => data.data.data
  });

  // Mutations
  const createUserMutation = useMutation({
    mutationFn: userService.createUser,
    onSuccess: () => {
      toast.success('Funcionário criado com sucesso!');
      queryClient.invalidateQueries(['users']);
      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar funcionário');
    }
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, userData }) => userService.updateUser(userId, userData),
    onSuccess: () => {
      toast.success('Funcionário atualizado com sucesso!');
      queryClient.invalidateQueries(['users']);
      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar funcionário');
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: userService.deleteUser,
    onSuccess: () => {
      toast.success('Funcionário desativado com sucesso!');
      queryClient.invalidateQueries(['users']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao desativar funcionário');
    }
  });

  const permanentlyDeleteUserMutation = useMutation({
    mutationFn: userService.permanentlyDeleteUser,
    onSuccess: () => {
      toast.success('Funcionário excluído permanentemente!');
      queryClient.invalidateQueries(['users']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir funcionário permanentemente');
    }
  });

  // Mutation para reset de senha pelo gerente
  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, newPassword }) => userService.resetUserPassword(userId, newPassword),
    onSuccess: (response, variables) => {
      toast.success(response.data.message || 'Senha resetada com sucesso!');
      closePasswordModal();
      queryClient.invalidateQueries(['users']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao resetar senha');
    }
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (editingUser) {
      // Não enviar senha se estiver editando
      const updateData = { ...formData };
      delete updateData.password;
      updateUserMutation.mutate({ userId: editingUser.id, userData: updateData });
    } else {
      createUserMutation.mutate(formData);
    }
  };

  const openModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '', // Não preencher senha ao editar
        full_name: user.full_name,
        role: user.role,
        is_active: user.is_active,
        birth_date: user.birth_date ? user.birth_date.split('T')[0] : '',
        hire_date: user.hire_date ? user.hire_date.split('T')[0] : ''
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        full_name: '',
        role: 'PRODUCAO',
        is_active: true,
        birth_date: '',
        hire_date: ''
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'PRODUCAO',
      is_active: true,
      birth_date: '',
      hire_date: ''
    });
    setShowPassword(false);
  };

  // Query para buscar documentos do usuário selecionado
  const { data: documentsData, refetch: refetchDocuments } = useQuery({
    queryKey: ['user-documents', selectedUserForDocuments],
    queryFn: () => userService.getUserDocuments(selectedUserForDocuments),
    enabled: !!selectedUserForDocuments && isDocumentsModalOpen,
  });

  const documents = useMemo(() => {
    if (!documentsData) return [];
    // A API retorna { success: true, data: [...] }
    if (Array.isArray(documentsData?.data)) return documentsData.data;
    if (Array.isArray(documentsData?.data?.data)) return documentsData.data.data;
    if (Array.isArray(documentsData)) return documentsData;
    return [];
  }, [documentsData]);

  // Mutation para upload de documento
  const uploadDocumentMutation = useMutation({
    mutationFn: ({ userId, formData }) => userService.uploadUserDocument(userId, formData),
    onSuccess: () => {
      toast.success('Documento enviado com sucesso!');
      refetchDocuments();
      setDocumentFormData({ document_type: '', description: '', document: null });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao enviar documento');
    }
  });

  // Mutation para deletar documento
  const deleteDocumentMutation = useMutation({
    mutationFn: ({ userId, documentId }) => userService.deleteUserDocument(userId, documentId),
    onSuccess: () => {
      toast.success('Documento deletado com sucesso!');
      refetchDocuments();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao deletar documento');
    }
  });

  const handleDocumentSubmit = async (e) => {
    e.preventDefault();
    if (!documentFormData.document || !documentFormData.document_type) {
      toast.error('Selecione um arquivo e informe o tipo de documento');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('document', documentFormData.document);
    formDataToSend.append('document_type', documentFormData.document_type);
    if (documentFormData.description) {
      formDataToSend.append('description', documentFormData.description);
    }

    uploadDocumentMutation.mutate({
      userId: selectedUserForDocuments,
      formData: formDataToSend
    });
  };

  const handleDownloadDocument = async (documentId) => {
    try {
      const response = await userService.downloadUserDocument(selectedUserForDocuments, documentId);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const doc = documents.find(d => d.id === documentId);
      link.download = doc?.document_name || 'documento';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao fazer download do documento');
    }
  };

  const openDocumentsModal = (user) => {
    setSelectedUserForDocuments(user.id);
    setIsDocumentsModalOpen(true);
    setDocumentFormData({ document_type: '', description: '', document: null });
  };

  const closeDocumentsModal = () => {
    setIsDocumentsModalOpen(false);
    setSelectedUserForDocuments(null);
    setDocumentFormData({ document_type: '', description: '', document: null });
  };

  // Funções para reset de senha
  const openPasswordModal = (user) => {
    setSelectedUserForPassword(user);
    setNewPassword('');
    setIsPasswordModalOpen(true);
  };

  const closePasswordModal = () => {
    setIsPasswordModalOpen(false);
    setSelectedUserForPassword(null);
    setNewPassword('');
  };

  const handleResetPasswordSubmit = (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    resetPasswordMutation.mutate({
      userId: selectedUserForPassword.id,
      newPassword: newPassword
    });
  };

  const handleDelete = async (user) => {
    setUserToDelete(user);
  };

  const confirmDeleteUser = () => {
    if (userToDelete) {
      deleteUserMutation.mutate(userToDelete.id);
      setUserToDelete(null);
    }
  };

  const handlePermanentDelete = async (user) => {
    const confirmMessage = `⚠️ ATENÇÃO: EXCLUSÃO PERMANENTE\n\nVocê está prestes a EXCLUIR PERMANENTEMENTE o funcionário:\n"${user.full_name}" (${user.email})\n\nEsta ação irá:\n✗ Remover TODOS os dados do funcionário\n✗ Remover holerites, jornadas, documentos\n✗ Remover histórico de trabalho\n\n⚠️ ESTA AÇÃO NÃO PODE SER DESFEITA!\n\nDigite o nome completo do funcionário para confirmar:`;

    const typedName = window.prompt(confirmMessage);

    if (typedName === user.full_name) {
      setUserToPermanentDelete(user);
    } else if (typedName !== null) {
      toast.error('Nome não corresponde. Exclusão cancelada por segurança.');
    }
  };

  const confirmPermanentDeleteUser = () => {
    if (userToPermanentDelete) {
      permanentlyDeleteUserMutation.mutate(userToPermanentDelete.id);
      setUserToPermanentDelete(null);
    }
  };

  const confirmDeleteDocument = () => {
    if (documentToDelete) {
      deleteDocumentMutation.mutate(documentToDelete);
      setDocumentToDelete(null);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      GERENCIA: 'badge-primary',
      RECEPCAO_COMPRAS_AGENDA: 'badge-secondary',
      RECEPCAO_PROJETOS: 'badge-success',
      PRODUCAO: 'badge-warning',
      MONTAGEM_CLIENTE: 'badge-info'
    };
    return colors[role] || 'badge-secondary';
  };

  const getStatusBadgeColor = (isActive) => {
    return isActive ? 'badge-success' : 'badge-danger';
  };

  // Filtrar funcionários localmente
  const filteredUsers = usersData?.users?.filter(user => {
    const matchesSearch = user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  }) || [];

  const roles = [
    { value: '', label: 'Todos os cargos' },
    { value: 'GERENCIA', label: 'Gerência' },
    { value: 'RECEPCAO_COMPRAS_AGENDA', label: 'Recepção - Compras & Agenda' },
    { value: 'RECEPCAO_PROJETOS', label: 'Recepção - Projetos' },
    { value: 'PRODUCAO', label: 'Produção' },
    { value: 'MONTAGEM_CLIENTE', label: 'Montagem - Cliente' }
  ];

  const statuses = [
    { value: '', label: 'Todos os status' },
    { value: 'true', label: 'Ativo' },
    { value: 'false', label: 'Inativo' }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-secondary-600">Carregando funcionários...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-md">
        Erro ao carregar funcionários: {error.response?.data?.message || error.message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">
            Gerenciar Funcionários
          </h1>
          <p className="text-secondary-600 mt-1">
            Adicione, edite e gerencie funcionários do sistema
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className="btn-primary"
        >
          <Plus className="h-5 w-5 mr-2" />
          Novo Funcionário
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Buscar
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
              <input
                type="text"
                placeholder="Nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Cargo
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="input"
            >
              {roles.map(role => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="input"
            >
              {statuses.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedRole('');
                setSelectedStatus('');
              }}
              className="btn-secondary w-full"
            >
              <Filter className="h-4 w-4 mr-2" />
              Limpar
            </button>
          </div>
        </div>
      </div>

      {/* Lista de funcionários */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Funcionário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Cargo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Data de Admissão
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Último acesso
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-secondary-500">
                    Nenhum funcionário encontrado
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-secondary-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center">
                          <span className="text-sm font-medium text-white">
                            {user.full_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-secondary-900">
                            {user.full_name}
                          </div>
                          <div className="text-sm text-secondary-500">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(user.is_active)}`}>
                        {user.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                      {user.hire_date
                        ? user.hire_date.split('T')[0].split('-').reverse().join('/')
                        : '-'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                      {user.last_login
                        ? user.last_login.split('T')[0].split('-').reverse().join('/')
                        : 'Nunca'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => openDocumentsModal(user)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Gerenciar documentos"
                        >
                          <FileText className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openPasswordModal(user)}
                          className="text-green-600 hover:text-green-900"
                          title="Resetar senha"
                        >
                          <Key className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openModal(user)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Editar funcionário"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          className="text-orange-600 hover:text-orange-900"
                          title="Desativar funcionário (reversível)"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handlePermanentDelete(user)}
                          className="text-red-600 hover:text-red-900 font-bold"
                          title="Excluir permanentemente"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de criação/edição */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={closeModal}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-secondary-900">
                      {editingUser ? 'Editar Funcionário' : 'Novo Funcionário'}
                    </h3>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="text-secondary-400 hover:text-secondary-600"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Nome Completo
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
                        <input
                          type="text"
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleInputChange}
                          className="input pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Email
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="input pl-10"
                          required
                        />
                      </div>
                    </div>

                    {!editingUser && (
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Senha
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
                          <input
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            className="input pl-10 pr-10"
                            required={!editingUser}
                            minLength="6"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2"
                          >
                            {showPassword ? (
                              <EyeOff className="h-5 w-5 text-secondary-400" />
                            ) : (
                              <Eye className="h-5 w-5 text-secondary-400" />
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Data de Nascimento
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
                        <input
                          type="date"
                          name="birth_date"
                          value={formData.birth_date}
                          onChange={handleInputChange}
                          className="input pl-10"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Data de Admissão
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
                        <input
                          type="date"
                          name="hire_date"
                          value={formData.hire_date}
                          onChange={handleInputChange}
                          className="input pl-10"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Cargo
                      </label>
                      <div className="relative">
                        <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
                        <select
                          name="role"
                          value={formData.role}
                          onChange={handleInputChange}
                          className="input pl-10"
                          required
                        >
                          <option value="GERENCIA">Gerência</option>
                          <option value="RECEPCAO_COMPRAS_AGENDA">Recepção - Compras & Agenda</option>
                          <option value="RECEPCAO_PROJETOS">Recepção - Projetos</option>
                          <option value="PRODUCAO">Produção</option>
                          <option value="MONTAGEM_CLIENTE">Montagem - Cliente</option>
                        </select>
                      </div>
                    </div>


                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-secondary-700">
                        Funcionário ativo
                      </label>
                    </div>
                  </div>
                </div>

                <div className="bg-secondary-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={createUserMutation.isLoading || updateUserMutation.isLoading}
                    className="btn-primary sm:ml-3 sm:w-auto"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {createUserMutation.isLoading || updateUserMutation.isLoading
                      ? 'Salvando...'
                      : editingUser
                        ? 'Atualizar'
                        : 'Criar'
                    }
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary mt-3 sm:mt-0 sm:w-auto"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Documentos */}
      {isDocumentsModalOpen && selectedUserForDocuments && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={closeDocumentsModal}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-secondary-900">
                    Documentos do Funcionário
                  </h3>
                  <button
                    type="button"
                    onClick={closeDocumentsModal}
                    className="text-secondary-400 hover:text-secondary-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {/* Formulário de Upload */}
                <form onSubmit={handleDocumentSubmit} className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-secondary-700 mb-3">Enviar Novo Documento</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Tipo de Documento *
                      </label>
                      <select
                        value={documentFormData.document_type}
                        onChange={(e) => setDocumentFormData({ ...documentFormData, document_type: e.target.value })}
                        className="input"
                        required
                      >
                        <option value="">Selecione...</option>
                        <option value="RG">RG</option>
                        <option value="CPF">CPF</option>
                        <option value="CTPS">CTPS (Carteira de Trabalho)</option>
                        <option value="CNH">CNH</option>
                        <option value="Comprovante de Residência">Comprovante de Residência</option>
                        <option value="Comprovante de Escolaridade">Comprovante de Escolaridade</option>
                        <option value="Contrato de Trabalho">Contrato de Trabalho</option>
                        <option value="Exame Médico">Exame Médico</option>
                        <option value="Outros">Outros</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Descrição (opcional)
                      </label>
                      <input
                        type="text"
                        value={documentFormData.description}
                        onChange={(e) => setDocumentFormData({ ...documentFormData, description: e.target.value })}
                        className="input"
                        placeholder="Ex: Cópia frente e verso"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Arquivo *
                      </label>
                      <div className="flex items-center gap-2">
                        <label className="flex-1 cursor-pointer">
                          <input
                            type="file"
                            onChange={(e) => setDocumentFormData({ ...documentFormData, document: e.target.files[0] })}
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                            required
                          />
                          <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                            <Upload className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-700">
                              {documentFormData.document ? documentFormData.document.name : 'Selecionar arquivo'}
                            </span>
                          </div>
                        </label>
                        <button
                          type="submit"
                          disabled={uploadDocumentMutation.isLoading}
                          className="btn-primary"
                        >
                          {uploadDocumentMutation.isLoading ? 'Enviando...' : 'Enviar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>

                {/* Lista de Documentos */}
                <div>
                  <h4 className="text-sm font-medium text-secondary-700 mb-3">Documentos Enviados</h4>
                  {documents.length === 0 ? (
                    <p className="text-sm text-secondary-500 text-center py-4">Nenhum documento cadastrado</p>
                  ) : (
                    <div className="space-y-2">
                      {documents.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <div className="flex items-center gap-3 flex-1">
                            <Paperclip className="h-5 w-5 text-gray-400" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-secondary-900">{doc.document_name}</p>
                              <p className="text-xs text-secondary-500">
                                {doc.document_type} • {doc.file_size ? `${(doc.file_size / 1024).toFixed(2)} KB` : ''} • {doc.created_at ? doc.created_at.split('T')[0].split('-').reverse().join('/') : ''}
                              </p>
                              {doc.description && (
                                <p className="text-xs text-secondary-400 mt-1">{doc.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleDownloadDocument(doc.id)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Download"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setDocumentToDelete({ userId: selectedUserForDocuments, documentId: doc.id })}
                              className="text-danger-600 hover:text-danger-900"
                              title="Deletar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reset de Senha */}
      {isPasswordModalOpen && selectedUserForPassword && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={closePasswordModal}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <form onSubmit={handleResetPasswordSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-secondary-900">
                      Resetar Senha do Funcionário
                    </h3>
                    <button
                      type="button"
                      onClick={closePasswordModal}
                      className="text-secondary-400 hover:text-secondary-600"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-700">
                      <strong>Funcionário:</strong> {selectedUserForPassword.full_name}<br />
                      <strong>Email:</strong> {selectedUserForPassword.email}
                    </p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Nova Senha
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="input pl-10 pr-10"
                        placeholder="Mínimo 6 caracteres"
                        minLength="6"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-5 w-5 text-secondary-400" />
                        ) : (
                          <Eye className="h-5 w-5 text-secondary-400" />
                        )}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-secondary-500">
                      A senha deve ter pelo menos 6 caracteres.
                    </p>
                  </div>

                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-700">
                      <strong>Atenção:</strong> Após resetar a senha, o funcionário precisará fazer login novamente.
                    </p>
                  </div>
                </div>

                <div className="bg-secondary-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={resetPasswordMutation.isLoading}
                    className="btn-primary sm:ml-3 sm:w-auto"
                  >
                    <Key className="h-4 w-4 mr-2" />
                    {resetPasswordMutation.isLoading ? 'Resetando...' : 'Resetar Senha'}
                  </button>
                  <button
                    type="button"
                    onClick={closePasswordModal}
                    className="btn-secondary mt-3 sm:mt-0 sm:w-auto"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!userToDelete}
        onConfirm={confirmDeleteUser}
        onCancel={() => setUserToDelete(null)}
        title="Desativar Funcionário"
        message={`Tem certeza que deseja desativar o usuário "${userToDelete?.full_name}"?`}
        confirmText="Desativar"
        type="warning"
      />

      <ConfirmModal
        show={!!userToPermanentDelete}
        onConfirm={confirmPermanentDeleteUser}
        onCancel={() => setUserToPermanentDelete(null)}
        title="Exclusão Permanente"
        message={`⚠️ ÚLTIMA CONFIRMAÇÃO:\n\nTem ABSOLUTA certeza de que deseja excluir permanentemente "${userToPermanentDelete?.full_name}"?\n\nTodos os dados serão perdidos para sempre!`}
        confirmText="Excluir Permanentemente"
        type="danger"
      />

      <ConfirmModal
        show={!!documentToDelete}
        onConfirm={confirmDeleteDocument}
        onCancel={() => setDocumentToDelete(null)}
        title="Deletar Documento"
        message="Tem certeza que deseja deletar este documento?"
        confirmText="Deletar"
        type="danger"
      />
    </div>
  );
};

export default Users; 